import React from 'react';

interface SnapGuide {
  type: 'vertical' | 'horizontal';
  position: number;
  label?: string;
}

interface SnapGuidesProps {
  guides: SnapGuide[];
  canvasWidth: number;
  canvasHeight: number;
}

export const SnapGuides: React.FC<SnapGuidesProps> = ({ guides }) => {
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 9998 }}>
      {guides.map((guide, index) => {
        if (guide.type === 'vertical') {
          return (
            <div key={`v-${index}`} className="absolute" style={{ left: `${guide.position}px`, top: 0, bottom: 0 }}>
              <div className="w-[1px] h-full bg-[#ff00ff] opacity-80" />
              {guide.label && (
                <div
                  className="absolute bg-[#ff00ff] text-white text-[10px] px-1 rounded"
                  style={{ top: '8px', left: '4px', whiteSpace: 'nowrap' }}
                >
                  {guide.label}
                </div>
              )}
            </div>
          );
        } else {
          return (
            <div key={`h-${index}`} className="absolute" style={{ top: `${guide.position}px`, left: 0, right: 0 }}>
              <div className="h-[1px] w-full bg-[#ff00ff] opacity-80" />
              {guide.label && (
                <div
                  className="absolute bg-[#ff00ff] text-white text-[10px] px-1 rounded"
                  style={{ left: '8px', top: '4px', whiteSpace: 'nowrap' }}
                >
                  {guide.label}
                </div>
              )}
            </div>
          );
        }
      })}
    </div>
  );
};

export interface SnapResult {
  x: number;
  y: number;
  guides: SnapGuide[];
}

export const calculateSnap = (
  x: number,
  y: number,
  width: number,
  height: number,
  canvasWidth: number,
  canvasHeight: number,
  threshold: number = 5
): SnapResult => {
  const guides: SnapGuide[] = [];
  let snappedX = x;
  let snappedY = y;

  // Calculate element edges
  const left = x;
  const right = x + width;
  const centerX = x + width / 2;
  const top = y;
  const bottom = y + height;
  const centerY = y + height / 2;

  // Canvas edges and center
  const canvasCenterX = canvasWidth / 2;
  const canvasCenterY = canvasHeight / 2;

  // Snap to canvas left edge
  if (Math.abs(left) < threshold) {
    snappedX = 0;
    guides.push({ type: 'vertical', position: 0, label: '0' });
  }
  // Snap to canvas right edge
  else if (Math.abs(right - canvasWidth) < threshold) {
    snappedX = canvasWidth - width;
    guides.push({ type: 'vertical', position: canvasWidth, label: `${canvasWidth}` });
  }
  // Snap to canvas center X
  else if (Math.abs(centerX - canvasCenterX) < threshold) {
    snappedX = canvasCenterX - width / 2;
    guides.push({ type: 'vertical', position: canvasCenterX, label: 'Center' });
  }

  // Snap to canvas top edge
  if (Math.abs(top) < threshold) {
    snappedY = 0;
    guides.push({ type: 'horizontal', position: 0, label: '0' });
  }
  // Snap to canvas bottom edge
  else if (Math.abs(bottom - canvasHeight) < threshold) {
    snappedY = canvasHeight - height;
    guides.push({ type: 'horizontal', position: canvasHeight, label: `${canvasHeight}` });
  }
  // Snap to canvas center Y
  else if (Math.abs(centerY - canvasCenterY) < threshold) {
    snappedY = canvasCenterY - height / 2;
    guides.push({ type: 'horizontal', position: canvasCenterY, label: 'Center' });
  }

  return { x: snappedX, y: snappedY, guides };
};

